import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import type { UserApplication, UserRole, WeeklyAvailability } from '../../types/user';
import { WEEKDAYS } from '../../../../shared/constants/application';

interface UserDashboardProps {
  role: UserRole;
  title: string;
  description: string;
  children?: ReactNode;
}

interface FieldProps {
  label: string;
  value?: string | number;
}

const sectionClass = 'mt-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm';

function Field({ label, value }: FieldProps) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm text-slate-900">{value || 'Not provided'}</dd>
    </div>
  );
}

function formatAddress(application: UserApplication): string {
  const { line1, line2, city, state, postalCode } = application.address;
  return [line1, line2, city, state, postalCode].filter(Boolean).join(', ');
}

function formatAvailability(application: Extract<UserApplication, { kind: 'caregiver' }>): string {
  const availability = application.availability as WeeklyAvailability | string;
  if (typeof availability === 'string') return availability;
  return WEEKDAYS.map((day) => `${day.slice(0, 3)}: ${availability[day]}`).join(' · ');
}

export function UserDashboard({ role, title, description, children }: UserDashboardProps): React.JSX.Element {
  const { user } = useAuth();

  if (!user || user.role !== role) {
    return <div className="px-6 py-16 text-center text-slate-600">Dashboard unavailable.</div>;
  }

  const { application } = user;
  const canEditApplication = (role === 'caregiver' || role === 'client')
    && application?.kind === role;
  const fullName = application
    ? `${application.firstName} ${application.lastName}`
    : user.name;

  return (
    <main className="mx-auto max-w-6xl px-6 py-10">
      <header className="flex flex-col gap-3 border-b border-slate-200 pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-mcc-darkest">{title}</h1>
          <p className="mt-1 text-slate-600">{description}</p>
        </div>
        <span className="w-fit rounded-full bg-mcc-lightest/40 px-3 py-1 text-xs font-semibold capitalize text-mcc-darkest">
          {role.replace('-', ' ')}
        </span>
      </header>

      <section className={sectionClass}>
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-mcc-darkest">Profile</h2>
          {canEditApplication && (
            <Link
              to="/profile/edit"
              className="rounded-md border border-mcc-neutral px-3 py-1.5 text-sm font-semibold text-mcc-neutral transition-colors hover:bg-mcc-lightest/20"
            >
              Edit profile
            </Link>
          )}
        </div>
        <dl className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Name" value={fullName} />
          <Field label="Email" value={application?.email || user.email} />
          {application && <Field label="Phone" value={application.phone} />}
          {application && <Field label="Address" value={formatAddress(application)} />}
          <Field label="Account ID" value={user.id} />
          <Field label="Last active" value={new Date(user.lastActiveAt).toLocaleString()} />
        </dl>
      </section>

      {application?.kind === 'caregiver' && (
        <section className={sectionClass}>
          <h2 className="text-lg font-semibold text-mcc-darkest">Caregiver application</h2>
          <dl className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Experience" value={`${application.yearsExperience} years`} />
            <Field label="Services" value={application.servicesOffered.join(', ')} />
            <Field label="Service areas" value={application.serviceAreas.join(', ')} />
            <Field label="Certifications" value={application.certifications.join(', ')} />
            <Field label="Languages" value={application.languages.join(', ')} />
            <Field label="Availability" value={formatAvailability(application)} />
          </dl>
          <p className="mt-5 text-sm text-slate-700">{application.caregivingApproach}</p>
        </section>
      )}

      {application?.kind === 'client' && (
        <section className={sectionClass}>
          <h2 className="text-lg font-semibold text-mcc-darkest">Care request</h2>
          <dl className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Care recipient" value={application.careRecipientName} />
            <Field label="Relationship" value={application.relationshipToRecipient} />
            <Field label="Requested services" value={application.requestedServices.join(', ')} />
          </dl>
          {application.careNotes && (
            <p className="mt-5 text-sm text-slate-700">{application.careNotes}</p>
          )}
        </section>
      )}

      {children}

      <section className="mt-6 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-7 text-center">
        <h2 className="font-semibold text-mcc-darkest">No activity yet</h2>
        <p className="mt-1 text-sm text-slate-600">
          Activity will appear here when it is available from the service API.
        </p>
      </section>
    </main>
  );
}
